import type {
  DocumentationStyle,
  HandoverNote,
  ScheduleEvent,
  SoapNote,
  UpdatePost,
} from '../types'
import { shiftSummary } from './cover'

/* ------------------------------------------------------------------ *
 * Structured shift handover
 *
 * The field list lives here rather than in the form so the writing screen
 * and the Team Updates card can never disagree about what a handover
 * contains, and so a future Supabase `handover_notes` table can be
 * generated from the same list.
 * ------------------------------------------------------------------ */

export type HandoverField = keyof HandoverNote

export interface HandoverFieldMeta {
  key: HandoverField
  label: string
  /** Plain-language prompt, written for a worker at the end of a shift. */
  placeholder: string
  rows: number
  /** Shown on the feed card without expanding, because the next worker and
   *  the participant's family need these at a glance. */
  headline?: boolean
}

export const HANDOVER_FIELDS: HandoverFieldMeta[] = [
  {
    key: 'summary',
    label: 'How the shift went',
    placeholder: 'A few lines on the shift overall...',
    rows: 3,
    headline: true,
  },
  {
    key: 'mood',
    label: 'Mood & wellbeing',
    placeholder: 'Energy, mood changes, sleep, how they seemed in themselves...',
    rows: 2,
  },
  {
    key: 'meals',
    label: 'Meals & drinks',
    placeholder: 'What was eaten and drunk, appetite, any support needed...',
    rows: 2,
  },
  {
    key: 'medication',
    label: 'Medication & health supports',
    placeholder: 'Medication given or refused, times, any health supports used...',
    rows: 2,
  },
  {
    key: 'activities',
    label: 'Activities & community access',
    placeholder: 'Where you went, what you did, who you saw...',
    rows: 2,
  },
  {
    key: 'personalCare',
    label: 'Personal care & routines',
    placeholder: 'Showering, dressing, routines followed or changed...',
    rows: 2,
  },
  {
    key: 'communication',
    label: 'Communication & sensory',
    placeholder: 'Communication that worked well, sensory needs, environment...',
    rows: 2,
  },
  {
    key: 'nextShift',
    label: 'Things the next worker should know',
    placeholder: 'e.g. Alex is tired — a quiet afternoon would suit better.',
    rows: 2,
    headline: true,
  },
  {
    key: 'followUp',
    label: 'Follow-up needed',
    placeholder: 'Anything someone has to pick up: a call, a form, a top-up...',
    rows: 2,
    headline: true,
  },
  {
    key: 'concerns',
    label: 'Concerns',
    placeholder: 'Anything that worried you, however small. Leave blank if none.',
    rows: 2,
    headline: true,
  },
]

export type SoapField = keyof SoapNote

export const SOAP_FIELDS: { key: SoapField; label: string; placeholder: string }[] = [
  { key: 'subjective', label: 'S — Subjective', placeholder: 'What was reported or said, in their words where you can.' },
  { key: 'objective', label: 'O — Objective', placeholder: 'What you observed and measured.' },
  { key: 'assessment', label: 'A — Assessment', placeholder: 'What you make of it.' },
  { key: 'plan', label: 'P — Plan', placeholder: 'What happens next, and who does it.' },
]

export const DOCUMENTATION_STYLE_LABELS: Record<DocumentationStyle, string> = {
  standard: 'Standard',
  soap: 'SOAP',
}

export function emptyHandoverNote(): HandoverNote {
  return {
    summary: '',
    mood: '',
    meals: '',
    medication: '',
    activities: '',
    personalCare: '',
    communication: '',
    nextShift: '',
    followUp: '',
    concerns: '',
  }
}

export function emptySoapNote(): SoapNote {
  return { subjective: '', objective: '', assessment: '', plan: '' }
}

function trimmed(value: string | undefined): string {
  return (value ?? '').trim()
}

/** Fields the worker actually filled in, in the order they were asked. */
export function filledHandoverFields(note: HandoverNote): { meta: HandoverFieldMeta; value: string }[] {
  return HANDOVER_FIELDS.map((meta) => ({ meta, value: trimmed(note[meta.key]) })).filter((f) => f.value)
}

export function filledSoapFields(soap: SoapNote | undefined): { label: string; value: string }[] {
  if (!soap) return []
  return SOAP_FIELDS.map((f) => ({ label: f.label, value: trimmed(soap[f.key]) })).filter((f) => f.value)
}

/** A handover with nothing written in it is not worth publishing. */
export function isHandoverEmpty(note: HandoverNote, soap?: SoapNote): boolean {
  return filledHandoverFields(note).length === 0 && filledSoapFields(soap).length === 0
}

/**
 * The plain-text form stored on the UpdatePost.
 *
 * Screens that only know how to show `text` — the Today summary strip, the
 * Visual mode cards, an SMS notification — keep working unchanged, while the
 * Team Updates feed reads the structured note for the richer card.
 */
export function handoverToText(note: HandoverNote, soap?: SoapNote): string {
  const lines = filledHandoverFields(note).map(({ meta, value }) => `${meta.label}: ${value}`)
  filledSoapFields(soap).forEach((f) => lines.push(`${f.label}: ${f.value}`))
  return lines.join('\n')
}

/** True for handovers written with the structured form (Stage 4 onwards). */
export function isStructuredHandover(update: UpdatePost): boolean {
  return update.type === 'handover' && !!update.handover
}

/** The one-line summary shown on a feed card before it is expanded. */
export function handoverSummaryLine(update: UpdatePost): string {
  const summary = trimmed(update.handover?.summary)
  if (summary) return summary
  const firstFilled = update.handover ? filledHandoverFields(update.handover)[0] : undefined
  if (firstFilled) return firstFilled.value
  const firstSoap = filledSoapFields(update.soap)[0]
  if (firstSoap) return firstSoap.value
  return update.text
}

/**
 * The things a reader must not have to expand a card to see: what needs
 * following up, what the next worker needs, and anything the worker flagged.
 */
export function handoverHighlights(update: UpdatePost): { meta: HandoverFieldMeta; value: string }[] {
  if (!update.handover) return []
  const note = update.handover
  return HANDOVER_FIELDS.filter((f) => f.headline && f.key !== 'summary')
    .map((meta) => ({ meta, value: trimmed(note[meta.key]) }))
    .filter((f) => f.value)
}

/* ------------------------------------------------------------------ *
 * Linking a handover to the shift it covers
 * ------------------------------------------------------------------ */

/** How far back a finished shift stays offerable as the handover's subject. */
const LINKABLE_DAYS_BACK = 7

/**
 * Support shifts the worker is on that a handover could reasonably belong to:
 * the last week through to tomorrow, most recent first. A worker with no
 * rostered shift can still write a handover — it simply links to nothing.
 */
export function linkableShiftsFor(
  events: ScheduleEvent[],
  userId: string,
  todayISO: string,
): ScheduleEvent[] {
  const from = new Date(todayISO + 'T00:00:00')
  from.setDate(from.getDate() - LINKABLE_DAYS_BACK)
  const to = new Date(todayISO + 'T00:00:00')
  to.setDate(to.getDate() + 1)
  const fromISO = from.toISOString().slice(0, 10)
  const toISO = to.toISOString().slice(0, 10)

  return events
    .filter((e) => e.type === 'shift')
    .filter((e) => e.assignees.some((a) => a.userId === userId && a.status !== 'declined'))
    .filter((e) => e.date >= fromISO && e.date <= toISO)
    .sort((a, b) => (a.date === b.date ? b.startTime.localeCompare(a.startTime) : b.date.localeCompare(a.date)))
}

/** Label for the shift a handover is attached to, on the form and the card. */
export function linkedShiftLabel(event: ScheduleEvent | undefined): string | undefined {
  return event ? shiftSummary(event) : undefined
}

